import React, { useState, useRef, useEffect } from "react"
import EditorComponent from "./utils/EditorComponent"
import { autoFormatScript, getScenes } from "./utils/formatUtils"
import { useLocation, useNavigate, useParams } from "react-router-dom"
import { getAccessToken } from "../../../services/authService"
import ScreenWritingPopUp from "./popupSliders/screenWritingPopUp"
import OnboardingOverlay from "../../Onboarding"
import { hasCompletedOnboarding } from "../../../utils/onboarding"
import LoaderSpin from "../../Common/LoaderSpin"
import { useDispatch, useSelector } from "react-redux"
import { setConnectionErrorOpen } from "../../../store/navSlice"
import {
  MdMenu,
  MdArrowBackIos,
  MdDashboard,
  MdFindReplace,
  MdOutlineDescription,
  MdSearch,
  MdTheaters,
} from "react-icons/md"
import { FiHelpCircle } from "react-icons/fi"
import ToolBar from "./ToolBar"
import FormatBar from "./FormatBar"
import SidePanel from "./sidepanel"
import DesktopMenuBar from "./DesktopMenuBar"
import {
  setCharacters,
  setCurrentDocumentType,
  setScenes,
  setEditor,
  setNotes,
} from "../../../store/scriptSlice"
import { Button, Tooltip } from "antd"
import { getPlanFeatures } from "../../../utils/access"
import PageTitleModal from "./modals/PageTitleModal"
import ScriptTitleEditor from "../ScriptTitleEditor"
import PageTitleModalTV from "./modals/PageTitleModalTV"
import PageTitleModalCommercial from "./modals/PageTitleModalCommercial"
import { toast } from "react-toastify"
import SaveAsModal from "../modals/SaveAsModal"
import { editFindAndReplace } from "./utils/scriptUtils"
import "./desktopSpacing.css"
import MediaToolsModal from "../../UserDashboard/MediaToolsModal"
import { loadScriptScenes } from "../../UserDashboard/loadScriptScenes"
import ImportCompleteNotice from "./modals/ImportCompleteNotice"
import useScriptAccess from '../../../utils/useScriptAccess'

const ScriptEditor = () => {
  const [scriptData, setScriptData] = useState(null)
  const scenes = useSelector((state) => state.script.scenes)
  const [currentScene, setCurrentScene] = useState(null)
  const [isPageTitleOpen, setIsPageTitleOpen] = useState(false)
  const [editorState, setEditorState] = useState(null)
  const [showScriptLoading, setShowScriptLoading] = useState(false)
  const [importCompletion, setImportCompletion] = useState(0)
  const handleImported = () => setImportCompletion(value => value + 1)

  const formatClasses = {
    "Film Script": [
      "sceneHeadings",
      "action",
      "character",
      "dialogue",
      "parentheses",
      "transition",
      "flashBack",
      "montage",
    ],
    "Commercial": [
      "sceneHeadings",
      "action",
      "character",
      "dialogue",
      "parentheses",
      "transition",
      "flashBack",
      "montage",
    ],
    "TV Script": [
      "coldOpening",
      "newAct",
      "sceneHeadings",
      "action",
      "character",
      "dialogue",
      "parentheses",
      "transition",
      "endAct",
      "flashBack",
    ],
    Treatment: ["tratmentPara"],
  }

  const getSctiptType = (type) => {
    return type === "FILM"
      ? "Film Script"
      : type === "TV"
      ? "TV Script"
      : type === "TREATMENT"
      ? "Treatment"
      : type === "COMMERCIAL"
      ? "Commercial"
      : ""
  }
  const [loading, setLoading] = useState(1)
  const [documentType, setDocumentType] = useState("")
  const [currentFormat, setCurrentFormat] = useState("")
  const [zoomLevel, setZoomLevel] = useState(100)
  const [isPopUpOpen, setIsPopUpOpen] = useState(false)
  const [showOnboarding, setShowOnboarding] = useState(false)
  const saveForMediaRef = useRef(null)
  const mediaRouteOpened = useRef(false)
  const mediaPosition = useRef(null)
  const [mediaMode, setMediaMode] = useState(null)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [charactersRequest, setCharactersRequest] = useState(0)
  const editorRef = useRef(null)
  const documentTypeRef = useRef(documentType) // Ref to keep track of documentType
  const notes = useSelector((state) => state.script.notes)

  const dispatch = useDispatch()
  const { id } = useParams()
  const scriptAccess = useScriptAccess(id)
  const canEdit = scriptAccess.canEdit === true
  const isOwner = scriptAccess.permission === 'OWNER'
  const sharedReadOnly = !isOwner && !canEdit
  const navigate = useNavigate()
  const location = useLocation()
  const [mobileProductionOpen, setMobileProductionOpen] = useState(!!location.state?.production)
  const canUseProduction = getPlanFeatures(JSON.parse(localStorage.getItem("user") || "null"))?.canUseCategoriesAndElements

  useEffect(() => {
    setMobileProductionOpen(!!location.state?.production)
  }, [location.state])

  const fetchScriptData = async () => {
    setLoading(1)
    const token = getAccessToken()
    const url = `${process.env.REACT_APP_DEV_API}/document/${id}`
    try {
      if (!token) {
        throw new Error("A valid session not found")
      }
      const res = await fetch(url, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      })
      if (!res.ok) {
        throw new Error("Error getting response")
      }
      const body = await res.json()
      const data = body.data

      setDocumentType(getSctiptType(data.type))
      dispatch(setCurrentDocumentType(data.type))
      setScriptData(data)
      setIsPopUpOpen(data.content === "<p></p>")
      setLoading(0)
    } catch (error) {
      setLoading(2)
      toast.error("Failed to load script")
      console.error(error)
    }
  }

  // useEffect(() => {
  //   setTimeout(() => {
  //   if(editorRef.current){
  //     console.log("setting notes")
  //   getAllNotes();
  //   }{
  //     console.log("no editor")
  //   }
  //   }, 1000);
  // }, [editorRef.current]);

  useEffect(() => {
    fetchScriptData().then(() => {
      setTimeout(() => {
        if (editorRef.current) {
          const editor = editorRef.current
          dispatch(setEditor(editor))
        }
      }, 3000)
    })
    dispatch(setScenes([]))
    dispatch(setCharacters({}))
  }, [])

  // Remember the last opened script so the mobile Scripts shortcut can reopen it.
  useEffect(() => {
    if (id) localStorage.setItem("lastOpenedScriptId", id)
  }, [id])

  // Restore the previous editing position and keep it updated while scrolling.
  useEffect(() => {
    const container = document.querySelector(".navwrapper .grow")
    if (!container || !id) return
    const key = `editorScroll_${id}`
    const saved = Number(localStorage.getItem(key) || 0)
    let restoreTimer
    if (saved > 0) {
      restoreTimer = setTimeout(() => {
        container.scrollTop = saved
      }, 500)
    }
    let saveTimer
    const onScroll = () => {
      clearTimeout(saveTimer)
      saveTimer = setTimeout(() => {
        localStorage.setItem(key, String(container.scrollTop))
      }, 300)
    }
    container.addEventListener("scroll", onScroll)
    return () => {
      container.removeEventListener("scroll", onScroll)
      clearTimeout(saveTimer)
      clearTimeout(restoreTimer)
    }
  }, [id])

  useEffect(() => {
    if (editorRef.current) {
      const editor = editorRef.current
      dispatch(setEditor(editor))
    }
  }, [editorRef.current])

  useEffect(() => {
    documentTypeRef.current = documentType
  }, [documentType])

  const handleTextTypeChange = (type) => {
    setCurrentFormat(type)
  }

  const handleZoomChange = (event) => {
    setZoomLevel(event.target.value)
  }

  const autoFormatEditorContent = () => {
    if (editorRef.current) {
      const editor = editorRef.current
      const content = editor.getContent({ format: "text" })
      const formattedContent = autoFormatScript(
        content,
        documentTypeRef.current
      )
      editor.setContent(formattedContent)
    }
  }

  let observer = null

  const openMobileSearch = () => {
    const editor = editorRef.current

    if (editor) {
      editor.focus()
      editor.execCommand("SearchReplace")

      // Cache DOM queries
      const bodycontent = document.querySelector(".tox-dialog__body-content")
      const body = document.querySelector(".tox-dialog")
      const findbutton = document.querySelector(".tox-dialog__footer-end")
      const settingsbutton = document.querySelector(".tox-dialog__footer-start")

      const hideFindResponsive = () => {
        bodycontent?.setAttribute("style", "padding-bottom:0; padding-top:0;")
        findbutton?.setAttribute("style", "margin-left:-8px;")

        if (body) {
          body.style.position = "fixed"
          body.style.top = "25%"
          body.style.left = "50%"
          body.style.transform = "translate(-50%, -25%) scale(0.7)"
          body.style.margin = "0"
          body.style.maxWidth = "100svw"
        }

        if (settingsbutton) {
          settingsbutton.style.marginLeft = "-10px"
        }

        editFindAndReplace()
      }

      const handleSettingsClick = () => {
        setTimeout(() => {
          const settingOptionsContainer = document.querySelector(
            ".tox-collection__group"
          )
          if (settingOptionsContainer) {
            settingOptionsContainer.childNodes.forEach((node) => {
              node.style.background = "white"
              node.style.color = "black"
            })
          }
        }, 0)
      }

      // Handle find button clicks
      findbutton?.addEventListener("click", () => {
        setTimeout(hideFindResponsive, 0)
      })

      // Enhanced settings button interaction handling
      if (settingsbutton) {
        // Handle both click and touch events
        const settingsHandler = (e) => {
          e.preventDefault()
          handleSettingsClick()
        }

        // Add event listeners to the entire settings button, not just firstChild
        settingsbutton.addEventListener("click", settingsHandler)
        settingsbutton.addEventListener("touchend", settingsHandler)

        // Optional: Add touch feedback
        settingsbutton.addEventListener("touchstart", (e) => {
          e.preventDefault()
          settingsbutton.style.opacity = "0.7"
        })

        settingsbutton.addEventListener("touchend", () => {
          settingsbutton.style.opacity = "1"
        })
      }

      hideFindResponsive()
    }
  }

  const openMedia = async (mode) => {
    if (!isOwner && (!canEdit || !scriptAccess.canUseAI)) return
    const editor = editorRef.current
    if (!editor || !saveForMediaRef.current) return
    const scroll = document.querySelector('.navwrapper .grow')
    mediaPosition.current = { bookmark: editor.selection.getBookmark(2, true), scrollTop: scroll?.scrollTop || 0,
      iframeScroll: editor.getWin()?.scrollY || 0 }
    try {
      await saveForMediaRef.current()
      setMobileMenuOpen(false)
      setMediaMode(mode)
    } catch (e) { toast.error(e.message) }
  }
  const closeMedia = () => {
    setMediaMode(null)
    requestAnimationFrame(() => {
      const editor = editorRef.current
      const position = mediaPosition.current
      if (!editor || !position) return
      editor.focus()
      editor.selection.moveToBookmark(position.bookmark)
      editor.getWin()?.scrollTo(0, position.iframeScroll)
      const scroll = document.querySelector('.navwrapper .grow')
      if (scroll) scroll.scrollTop = position.scrollTop
    })
  }
  const beforeMediaGenerate = async () => {
    if (!isOwner && (!canEdit || !scriptAccess.canUseAI)) throw new Error('An active paid AI entitlement is required for this shared screenplay.')
    if (!saveForMediaRef.current) throw new Error('The screenplay is not ready to save.')
    await saveForMediaRef.current()
    return loadScriptScenes(id)
  }

  useEffect(() => {
    if (!editorState || mediaRouteOpened.current || !['storyboard', 'video'].includes(location.state?.media)) return
    mediaRouteOpened.current = true
    openMedia(location.state.media)
    // Open the mobile script-menu entry only once after the editor is ready.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editorState, location.state])

  const isEditorFocus = useSelector((state) => state.script.isEditorFocus)

  useEffect(() => {
    if (!editorState) return

    const body = editorState.getBody()
    if (
      [
        "character",
        "coldOpening",
        "dialogue",
        "parentheses",
        "transition",
      ].includes(currentFormat) &&
      isEditorFocus
    ) {
      body.setAttribute("data-hide-placeholder", "")
    } else body.removeAttribute("data-hide-placeholder")
  }, [currentFormat, editorState, isEditorFocus])

  return (
    <>
      {mediaMode && !sharedReadOnly && scriptData && <MediaToolsModal script={scriptData} mode={mediaMode} onClose={closeMedia}
        beforeGenerate={beforeMediaGenerate} onCharacters={() => { closeMedia(); setMobileMenuOpen(true); setCharactersRequest(value => value + 1) }} />}
      {isPageTitleOpen && canEdit && scriptData?.type && (
        <>
          {scriptData.type === "COMMERCIAL" ? (
            <PageTitleModalCommercial
              scriptData={scriptData}
              setIsPageTitleOpen={setIsPageTitleOpen}
            />
          ) : scriptData.type === "TV" ? (
            <PageTitleModalTV
              scriptData={scriptData}
              setIsPageTitleOpen={setIsPageTitleOpen}
            />
          ) : (
            <PageTitleModal
              scriptData={scriptData}
              setIsPageTitleOpen={setIsPageTitleOpen}
            />
          )}
        </>
      )}
      {showScriptLoading && (
        <div className="fixed gap-2 bg-[#f3f3f3] top-[50svh] translate-x-[-50%] z-[80] left-[50vw] p-4 rounded shadow flex items-center">
          <LoaderSpin />
          <p className="text-[#868686]">Loading script</p>
        </div>
      )}
      <div className="screenwriting-editor bg-white flex flex-col h-full lg:bg-[#fbfbfb]">
        <nav className="lg:hidden p-4 flex border-b border-[#0000001A] justify-between items-baseline">
          <div className="flex gap-1">
            <button
              className="h-fit pt-1"
              onClick={() => {
                navigate(isOwner ? `/scripts/${id}` : '/home', { state: isOwner ? undefined : { section: 'shared' } })
              }}
            >
              <MdArrowBackIos size={22} color="#292929" />
            </button>
            <div className="">
              <h1 className="text-[#292929] font-bold text-[20px]" title={documentType}>
                Screenwriting
              </h1>
              <h2 className="text-[#868686] text-[15px] truncate max-w-[calc(100svw-70px)]">
                {scriptData?.title}
              </h2>
            </div>
          </div>
          <div></div>
          <div className="flex items-center gap-3">
            {!sharedReadOnly && <Button type="text" aria-label="Screenwriting menu" icon={<MdMenu size={24} />} onClick={() => setMobileMenuOpen(true)} />}
            <button
              className="pt-1"
              aria-label="Dashboard"
              onClick={() => navigate("/home")}
            >
              <MdDashboard size={24} color="#0099FF" />
            </button>
            {!sharedReadOnly && <button
              className="pt-1"
              aria-label="Help - reopen onboarding"
              onClick={() => setShowOnboarding(true)}
            >
              <FiHelpCircle size={24} color="#AAAAAA" />
            </button>}
            {canEdit && <button className="pt-1">
              <MdFindReplace
                size={28}
                color="#AAAAAA"
                onClick={openMobileSearch}
              />
            </button>}
          </div>
        </nav>
        <nav className="max-lg:hidden bg-white shadow p-4 py-3  flex flex-col border-b border-[#0000001A] gap-4">
          <div className="flex w-full relative  items-center justify-start">
            <div className="z-[80] basis-[50%]">
              {canEdit && <DesktopMenuBar
                setShowScriptLoading={setShowScriptLoading}
                documentType={documentType}
                editor={editorState}
                scriptData={scriptData}
                onImported={handleImported}
              />}
            </div>
            <div className="absolute left-[50%] max-w-[500px] translate-x-[-50%] px-4 py-1 w-fit h-fit">
              {scriptData && (
                <ScriptTitleEditor
                  scriptId={id}
                  title={scriptData.title}
                  canEdit={canEdit && isOwner}
                  onSaved={(updated) => setScriptData((prev) => ({ ...prev, title: updated.title }))}
                  className="text-[#585858] font-bold text-[18px] max-w-[500px]"
                  textClassName="truncate"
                  inputClassName="text-[18px] w-[360px]"
                />
              )}
            </div>
            <div className="ml-auto flex items-center gap-4">
              <button
                className="flex items-center gap-1 text-[14px] font-bold text-[#0099FF] border border-[#0099FF] rounded-full px-3 py-1 hover:bg-[#EAF6FF] transition-colors"
                aria-label="Dashboard"
                onClick={() => navigate("/home")}
              >
                <MdDashboard size={18} /> Dashboard
              </button>
              {!sharedReadOnly && <Tooltip placement={"bottom"} title="Help - reopen onboarding">
                <button
                  className="text-[#AAAAAA] hover:text-[#0099FF] transition-colors"
                  aria-label="Help - reopen onboarding"
                  onClick={() => setShowOnboarding(true)}
                >
                  <FiHelpCircle size={22} />
                </button>
              </Tooltip>}
            </div>
          </div>
        </nav>

        {loading === 0 && (
          <>
            <div className="lg:hidden">
              {canEdit && scriptData?.type !== "TREATMENT" && (
                <ToolBar
                  scenes={scenes}
                  editor={editorRef.current}
                  documentType={documentType}
                  formatClasses={formatClasses}
                  setCurrentFormat={setCurrentFormat}
                  currentFormat={currentFormat}
                />
              )}
            </div>
            <div className="overflow-y-auto grow flex gap-3 justify-center ">
              {!sharedReadOnly && <SidePanel scriptData={scriptData} scriptAccess={scriptAccess} editor={editorRef.current}
                mobileProductionOpen={canEdit && mobileProductionOpen && scriptData.type !== "TREATMENT" && canUseProduction}
                onCloseMobileProduction={() => setMobileProductionOpen(false)}
                mobileMenuOpen={mobileMenuOpen} onCloseMobileMenu={() => setMobileMenuOpen(false)} onOpenMedia={openMedia} charactersRequest={charactersRequest} />}
              <div className="max-lg:w-full h-[calc(100%-12px)] max-lg:h-full grow w-fit max-w-[8.5in] lg:max-w-[11in] max-lg:mx-auto shadow-xl lg:p-3 lg:mt-3 border rounded-xl flex flex-col">
                {canEdit && <div className="desktop-editor-toolbar max-lg:hidden flex gap-2 items-center mb-3">
                  {scriptData?.type !== "TREATMENT" && (
                    <ToolBar
                      scenes={scenes}
                      currentScene={currentScene}
                      editor={editorRef?.current}
                      documentType={documentType}
                      formatClasses={formatClasses}
                      setCurrentFormat={setCurrentFormat}
                      currentFormat={currentFormat}
                    />
                  )}
                  <FormatBar
                    zoomLevel={zoomLevel}
                    setZoomLevel={setZoomLevel}
                    editor={editorRef?.current}
                  />
                  <Tooltip placement={"bottom"} title="Page title">
                    <button
                      className="hover:bg-[#ececec] active:text-[#0099FF] -ms-4 text-[#777777] p-1 rounded"
                      onClick={() => {
                        setIsPageTitleOpen(true)
                      }}
                    >
                      <MdOutlineDescription size={20} />
                    </button>
                  </Tooltip>
                </div>}
                <div className="max-w-[8.5in] lg:max-w-full lg:h-[calc(100%)] w-full grow mx-auto h-[11in] lg:border relative">
                  <EditorComponent
                    scriptAccess={scriptAccess}
                    editorRef={editorRef}
                    saveForMediaRef={saveForMediaRef}
                    formatClasses={formatClasses}
                    content={scriptData.content}
                    documentType={documentType}
                    setCurrentFormat={setCurrentFormat}
                    documentTypeRef={documentTypeRef}
                    scenes={scenes}
                    scriptData={scriptData}
                    setEditorState={setEditorState}
                    zoomVal={zoomLevel}
                  />
                </div>
              </div>
            </div>
          </>
        )}
        {loading === 1 && (
          <div className="mt-[20%] flex justify-center">
            <LoaderSpin size={30} />{" "}
          </div>
        )}
        {isPopUpOpen && canEdit && (
          <ScreenWritingPopUp
            isOpen={isPopUpOpen}
            setIsOpen={setIsPopUpOpen}
            editor={editorState}
            documentType={documentType}
            onContinue={() => { if (!hasCompletedOnboarding()) setShowOnboarding(true) }}
            onImported={handleImported}
          />
        )}
        {showOnboarding && !sharedReadOnly && <OnboardingOverlay onDone={() => setShowOnboarding(false)} />}
        <ImportCompleteNotice completion={importCompletion} scriptId={id}
          saveScreenplay={async () => {
            if (!saveForMediaRef.current) throw new Error('The screenplay is not ready to save.')
            await saveForMediaRef.current()
          }} />

      </div>
    </>
  )
}

export default ScriptEditor
